/* © CNES */
import { Toolbar } from 'primereact/toolbar';
import { useEffect, useState } from 'react';
import { useGetItemsAvailabilities } from '../../../api/items/useGetItemAvailability';
import { CollectionDetails } from '../../../models/CollectionDetails';
import { ItemAvailabilityFile } from '../../../models/ItemAvailability';
import { Process } from '../../../models/Process';
import { ProductsAdded } from '../../../models/ProductsAdded';
import { ItemsData } from '../ItemsData/ItemsData';
import { AddedToolbar } from './AddedToolbar/AddedToolbar';
import './InnerAdded.scss';
import { ProcessDialog } from './ProcessDialog/ProcessDialog';

/**
 * InnerAdded displays a list of products added by user in MyData.
 * @param productsAdded The products added by user logged in.
 * @param setProductsAdded Function to set the products added.
 * @param setActiveIndex Function to set the active index on page change in router.
 * @param setDoRefetchJobs Function to refect jobs when processing products.
 * @param listProcess list of processes that user can choose from.
 * @param setDownloadProgress Function to setDownloadProgress.
 * @param refetchGetSavedProduct Function to refetch GetSavedProduct.
 * @returns Element.
 */
export const InnerAdded = ({
  productsAdded,
  setProductsAdded,
  setActiveIndex,
  setDoRefetchJobs,
  listProcess,
  setDownloadProgress,
  refetchGetSavedProduct,
}: {
  productsAdded: ProductsAdded;
  setProductsAdded: Function;
  setActiveIndex: Function;
  setDoRefetchJobs: Function;
  listProcess: Process[];
  setDownloadProgress: Function;
  refetchGetSavedProduct: Function;
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [visibleDialog, setVisibleDialog] = useState(false);
  const [itemsAvailabilitySummary, setItemsAvailabilitySummary] = useState<
    { productId: string; available: boolean | undefined }[]
  >([]);

  /**
   * fetch the availability status of items based on their product IDs.
  const {
    data: itemAvailabilities,
    isSuccess: isAvailabilitiesSuccess,
    isLoading: isAvailabilitiesLoading,
  } = useGetItemsAvailabilities({
    availability: productsAdded.collectionsDetails
      ?.filter((product) => product.endpointURL)
      ?.map((product) => ({
        endpointURL: product.endpointURL,
        href: product.href,
      })),
  });
  */
  const {
    data: itemAvailabilities,
    isSuccess: isAvailabilitiesSuccess,
    isLoading: isAvailabilitiesLoading,
  } = useGetItemsAvailabilities({
    availability: productsAdded.collectionsDetails
      ?.filter((product) => product.endpointURL)
      ?.map((product) => ({
        id: product.id,
        bbox: [],
        type: '',
        links: [{ rel: '', type: '', href: '' }],
        assets: {
          data: {
            href: product.href,
            type: '',
            "file:checksum": product.href,
          },
          quicklook: {
            href: '',
            type: '',
            "file:checksum": '',
          }
        },
        properties: {"sat:absolute_orbit": 0}
      }))
  });

  useEffect(() => {
    if (isAvailabilitiesSuccess && itemAvailabilities?.status !== 500) {
      setItemsAvailabilitySummary(
        itemAvailabilities?.products.map((product) => ({
          productId: product.id,
          available:
            product.files?.every(
              (file: ItemAvailabilityFile) => file.available
            ) ?? false,
        }))
      );
    }

    if (itemAvailabilities?.status === 500 || isAvailabilitiesLoading) {
      setItemsAvailabilitySummary(
        productsAdded?.collectionsDetails?.map(
          (collection: CollectionDetails) => {
            return { productId: collection.id, available: undefined };
          }
        )
      );
    }
  }, [
    itemAvailabilities,
    productsAdded,
    isAvailabilitiesSuccess,
    isAvailabilitiesLoading,
  ]);

  /**
   * handle Visible Dialog.
   */
  const handleVisibleDialog = () => {
    setVisibleDialog(!visibleDialog);
    if (!visibleDialog) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  /**
   * centerContent
   */
  const centerContent = (
    <AddedToolbar
      productsAdded={productsAdded}
      selectedItems={selectedItems}
      setProductsAdded={setProductsAdded}
      setSelectedItems={setSelectedItems}
      handleVisibleDialog={handleVisibleDialog}
      setDownloadProgress={setDownloadProgress}
      itemsAvailabilitySummary={itemsAvailabilitySummary}
    />
  );

  return (
    <>
      <div className="InnerAdded">
        <ItemsData
          productsAdded={productsAdded}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          itemsAvailabilitySummary={itemsAvailabilitySummary}
          refetchGetSavedProduct={refetchGetSavedProduct}
          itemAvailabilities={itemAvailabilities}
        />
        <ProcessDialog
          productsProcess={selectedItems}
          visibleDialog={visibleDialog}
          handleVisibleDialog={handleVisibleDialog}
          setActiveIndex={setActiveIndex}
          setDoRefetchJobs={setDoRefetchJobs}
          listProcess={listProcess}
          itemsAvailabilitySummary={itemsAvailabilitySummary}
        />
      </div>
      <div className="ToolbarContainer">
        <Toolbar center={centerContent} />
      </div>
    </>
  );
};
